import { getCookieCustom } from '../utils/cookies';

type AuthCookieUser = {
 currentlyActiveDb?: string;
 databases?: string[];
};

const slugify = (value: string) =>
 value
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const getCurrentDatabaseSlug = () => {
 const userCookie = getCookieCustom('user');
 if (!userCookie) {
  return '';
 }

 try {
  const user = JSON.parse(userCookie) as AuthCookieUser;
  if (!user.currentlyActiveDb || !Array.isArray(user.databases)) {
   return '';
  }

  const activeDatabase = user.databases.find((databaseItem) =>
   databaseItem.startsWith(`${user.currentlyActiveDb}:`),
  );
  if (!activeDatabase) {
   return '';
  }

  const databaseName = activeDatabase.split(':').slice(1).join(':').trim();
  if (!databaseName) {
   return '';
  }

  return slugify(databaseName);
 } catch {
  return '';
 }
};

const storagePrefix = [import.meta.env.VITE_APP_NAME, getCurrentDatabaseSlug()]
 .filter(Boolean)
 .join('_');

const buildKey = (name: string) => `${storagePrefix}_${name}`;

const parseStored = (raw: string | null) => {
 if (!raw) return null;
 try {
  return JSON.parse(raw);
 } catch {
  return raw;
 }
};

const getConfigStorageItem = (key: string) => {
 return parseStored(localStorage.getItem(buildKey(key)));
};

const setConfigStorageItem = ({
 page,
 key,
 value,
}: {
 page?: string;
 key: string;
 value: string | object;
}) => {
 if (!page) {
  const serialized =
   typeof value === 'string' ? value : JSON.stringify(value);
  localStorage.setItem(buildKey(key), serialized);
  return;
 }

 const existing = getConfigStorageItem(page);
 const currentObject =
  existing && typeof existing === 'object' && !Array.isArray(existing)
   ? (existing as Record<string, unknown>)
   : {};

 localStorage.setItem(
  buildKey(page),
  JSON.stringify({ ...currentObject, [key]: value }),
 );
};

const removeConfigStorageItem = ({
 page,
 key,
}: {
 page?: string;
 key: string;
}) => {
 if (!page) {
  localStorage.removeItem(buildKey(key));
  return;
 }

 const existing = getConfigStorageItem(page);
 if (!existing || typeof existing !== 'object' || Array.isArray(existing)) {
  return;
 }

 const next: Record<string, unknown> = { ...(existing as Record<string, unknown>) };
 delete next[key];
 localStorage.setItem(buildKey(page), JSON.stringify(next));
};

export {
 getConfigStorageItem,
 removeConfigStorageItem,
 setConfigStorageItem,
 storagePrefix,
};
